/**
 * Validated client-side environment variables.
 *
 * Vite only exposes vars prefixed with `VITE_` to the browser bundle.
 * This module reads them once at module load.
 *
 * The Supabase keys are technically required for auth to function, but the
 * landing page itself doesn't use Supabase. To let the marketing page render
 * cleanly before Supabase is set up, we soft-fail with a console warning and
 * placeholder values. The Supabase client will still construct (and any auth
 * call will fail at the call site, which is the correct behavior).
 *
 * Set real values in `frontend/.env.local` once your Supabase project exists.
 */

type Env = {
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  hasRealSupabase: boolean;
};

const PLACEHOLDER_URL = "https://placeholder.supabase.co";
const PLACEHOLDER_KEY = "placeholder-anon-key";

function readOptional(value: string | undefined): { value: string; missing: boolean } {
  if (!value || value.startsWith("https://PROJECT_REF") || value === "") {
    return { value: "", missing: true };
  }
  return { value, missing: false };
}

const supabaseUrlInput = readOptional(import.meta.env.VITE_SUPABASE_URL);
const supabaseKeyInput = readOptional(import.meta.env.VITE_SUPABASE_ANON_KEY);

const hasRealSupabase = !supabaseUrlInput.missing && !supabaseKeyInput.missing;

if (!hasRealSupabase && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn(
    "[vegr] VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY are not set. " +
      "The landing page will render, but auth and API calls will fail. " +
      "Copy frontend/.env.example to frontend/.env.local and fill in your Supabase project values.",
  );
}

export const env: Env = {
  apiUrl: import.meta.env.VITE_API_URL ?? "/api/v1",
  supabaseUrl: hasRealSupabase ? supabaseUrlInput.value : PLACEHOLDER_URL,
  supabaseAnonKey: hasRealSupabase ? supabaseKeyInput.value : PLACEHOLDER_KEY,
  hasRealSupabase,
};
